import {useListSorting} from '@sollapay/ui';
import {useDeferredValue, useState} from 'react';

import {useJsonStorage, useListViewPreference} from '@/hooks';

import {useFilteredTrustAccounts} from './useFilteredTrustAccounts';
import {DEFAULT_FILTER_STATE} from '../config';
import {
  parseStoredFilterState,
  parseStoredSorting,
  toStoredActiveFilter,
  toStoredSorting
} from '../utils';

import type {FilterableTrustAccount} from './useFilteredTrustAccounts';
import type {TrustAccountFilterFields} from '../config';
import type {TrustAccountFilterState} from '../utils';
import type {ListSortConfig, ListSortingState, ListView} from '@sollapay/ui/components';

/** `viewKey` also keys the remembered filter and sort, so each list keeps its own. */
export interface TrustAccountListToolbarOptions<T> {
  sortConfig: ListSortConfig<T>;
  filterFields: TrustAccountFilterFields<T>;
  viewKey: string;
  /** The view shown until the user picks one; the table when omitted. */
  defaultView?: ListView;
}

const NO_ACCOUNTS: never[] = [];
const UNSORTED: ListSortingState = [];

/**
 * `visibleAccounts` stays undefined while `accounts` is, so callers can tell loading from no
 * match. The filter and the sort are remembered per list (shared by its table and card views);
 * the search text is not.
 */
export function useTrustAccountListToolbar<T extends FilterableTrustAccount>(
  accounts: T[] | undefined,
  {sortConfig, filterFields, viewKey, defaultView = 'table'}: TrustAccountListToolbarOptions<T>
) {
  const [searchText, setSearchText] = useState('');
  // Typing stays responsive on long lists; filtering catches up a frame later.
  const deferredSearchText = useDeferredValue(searchText);
  const [filter, setFilter] = useJsonStorage<TrustAccountFilterState>(
    `${viewKey}:filter`,
    DEFAULT_FILTER_STATE,
    parseStoredFilterState,
    toStoredActiveFilter
  );
  const [storedSorting, setStoredSorting] = useJsonStorage(
    `${viewKey}:sorting`,
    UNSORTED,
    parseStoredSorting,
    toStoredSorting
  );
  const {view, setPreferredView} = useListViewPreference(viewKey, defaultView);
  const filtered = useFilteredTrustAccounts(
    accounts ?? NO_ACCOUNTS,
    deferredSearchText,
    filter,
    filterFields
  );
  const {sorting, onSortingChange, sortedItems} = useListSorting(filtered, sortConfig, {
    sorting: storedSorting,
    onSortingChange: setStoredSorting
  });

  return {
    toolbar: {
      searchText,
      onSearchChange: setSearchText,
      filter,
      onFilterChange: setFilter,
      sortConfig,
      sorting,
      onSortingChange,
      view,
      onViewChange: setPreferredView
    },
    visibleAccounts: accounts && sortedItems,
    clearSearch: () => setSearchText(''),
    clearFilters: () => setFilter(DEFAULT_FILTER_STATE)
  };
}
